import { useState, useEffect, useCallback } from 'react';
import { notificationRulesApi, notificationsApi, NotificationsParams } from '@/api/notifications';
import type {
  NotificationRule,
  NotificationRuleCreate,
  NotificationRuleListItem,
  NotificationListItem,
  Notification,
} from '@/types/data';
import { useAuthStore } from '@/stores/authStore';
import { Bell, Settings, AlertTriangle, Info, CheckCircle, Eye } from 'lucide-react';

type ViewMode = 'notifications' | 'rules';

const METRIC_OPTIONS = [
  { value: 'daily_value', label: '日次合計値' },
  { value: 'label_count', label: 'ラベル数' },
];

const CONDITION_OPTIONS = [
  { value: 'gt', label: 'より大きい (>)' },
  { value: 'lt', label: 'より小さい (<)' },
  { value: 'gte', label: '以上 (>=)' },
  { value: 'lte', label: '以下 (<=)' },
  { value: 'eq', label: '等しい (=)' },
  { value: 'change_percent', label: '変化率 (%)' },
];

const RULE_TYPE_OPTIONS = [
  { value: 'threshold', label: '閾値' },
  { value: 'change_percent', label: '変化率' },
];

export default function Notifications() {
  const { accessibleCompanies } = useAuthStore();
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('notifications');

  // 通知一覧
  const [notifications, setNotifications] = useState<NotificationListItem[]>([]);
  const [totalNotifications, setTotalNotifications] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filterUnread, setFilterUnread] = useState<boolean | undefined>(undefined);

  // 通知詳細
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  // ルール管理
  const [rules, setRules] = useState<NotificationRuleListItem[]>([]);
  const [isLoadingRules, setIsLoadingRules] = useState(false);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editingRule, setEditingRule] = useState<NotificationRule | null>(null);

  // ルール作成/編集フォーム
  const [ruleName, setRuleName] = useState('');
  const [ruleDescription, setRuleDescription] = useState('');
  const [ruleType, setRuleType] = useState('threshold');
  const [metric, setMetric] = useState('daily_value');
  const [condition, setCondition] = useState('lt');
  const [threshold, setThreshold] = useState<number>(0);
  const [notificationChannels, setNotificationChannels] = useState<string[]>(['email']);

  // 初期化
  useEffect(() => {
    if (accessibleCompanies.length > 0 && !selectedCompanyId) {
      setSelectedCompanyId(accessibleCompanies[0].id);
    }
  }, [accessibleCompanies, selectedCompanyId]);

  // 通知一覧取得
  const fetchNotifications = useCallback(async () => {
    if (!selectedCompanyId) return;
    setIsLoadingNotifications(true);
    try {
      const params: NotificationsParams = {
        company_id: selectedCompanyId,
        page: currentPage,
        per_page: 20,
      };
      if (filterUnread !== undefined) {
        params.is_read = !filterUnread;
      }
      const response = await notificationsApi.getNotifications(params);
      setNotifications(response.items);
      setTotalNotifications(response.total);
    } catch (error) {
      console.error('通知一覧取得エラー:', error);
    } finally {
      setIsLoadingNotifications(false);
    }
  }, [selectedCompanyId, currentPage, filterUnread]);

  // 未読件数取得
  const fetchUnreadCount = useCallback(async () => {
    if (!selectedCompanyId) return;
    try {
      const response = await notificationsApi.getUnreadCount(selectedCompanyId);
      setUnreadCount(response.count);
    } catch (error) {
      console.error('未読件数取得エラー:', error);
    }
  }, [selectedCompanyId]);

  // ルール一覧取得
  const fetchRules = useCallback(async () => {
    if (!selectedCompanyId) return;
    setIsLoadingRules(true);
    try {
      const data = await notificationRulesApi.getRules(selectedCompanyId);
      setRules(data);
    } catch (error) {
      console.error('通知ルール取得エラー:', error);
    } finally {
      setIsLoadingRules(false);
    }
  }, [selectedCompanyId]);

  useEffect(() => {
    if (viewMode === 'notifications') {
      fetchNotifications();
      fetchUnreadCount();
    } else {
      fetchRules();
    }
  }, [viewMode, fetchNotifications, fetchUnreadCount, fetchRules]);

  // 通知詳細取得
  const viewNotificationDetail = async (notification: NotificationListItem) => {
    setIsLoadingDetail(true);
    try {
      const detail = await notificationsApi.getNotification(notification.id);
      setSelectedNotification(detail);
      if (!notification.is_read) {
        await notificationsApi.markAsRead(notification.id);
        fetchNotifications();
        fetchUnreadCount();
      }
    } catch (error) {
      console.error('通知詳細取得エラー:', error);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  // 全て既読にする
  const markAllAsRead = async () => {
    if (!selectedCompanyId) return;
    try {
      await notificationsApi.markAllAsRead(selectedCompanyId);
      fetchNotifications();
      fetchUnreadCount();
    } catch (error) {
      console.error('既読処理エラー:', error);
    }
  };

  // ルールモーダルを開く
  const openRuleModal = async (rule?: NotificationRuleListItem) => {
    if (rule) {
      const fullRule = await notificationRulesApi.getRule(rule.id);
      setEditingRule(fullRule);
      setRuleName(fullRule.name);
      setRuleDescription(fullRule.description || '');
      setRuleType(fullRule.rule_type);
      setMetric(fullRule.metric);
      setCondition(fullRule.condition);
      setThreshold(fullRule.threshold);
      setNotificationChannels(fullRule.notification_channels);
    } else {
      setEditingRule(null);
      setRuleName('');
      setRuleDescription('');
      setRuleType('threshold');
      setMetric('daily_value');
      setCondition('lt');
      setThreshold(0);
      setNotificationChannels(['email']);
    }
    setShowRuleModal(true);
  };

  // ルール保存
  const saveRule = async () => {
    if (!selectedCompanyId || !ruleName) return;

    const data: NotificationRuleCreate = {
      company_id: selectedCompanyId,
      name: ruleName,
      description: ruleDescription || undefined,
      rule_type: ruleType,
      metric,
      condition,
      threshold,
      notification_channels: notificationChannels,
    };

    try {
      if (editingRule) {
        await notificationRulesApi.updateRule(editingRule.id, data);
      } else {
        await notificationRulesApi.createRule(data);
      }
      setShowRuleModal(false);
      fetchRules();
    } catch (error) {
      console.error('ルール保存エラー:', error);
      alert('ルールの保存に失敗しました');
    }
  };

  // ルール削除
  const deleteRule = async (ruleId: number) => {
    if (!confirm('この通知ルールを削除しますか？')) return;
    try {
      await notificationRulesApi.deleteRule(ruleId);
      fetchRules();
    } catch (error) {
      console.error('ルール削除エラー:', error);
      alert('ルールの削除に失敗しました');
    }
  };

  // ルール有効/無効切り替え
  const toggleRuleActive = async (rule: NotificationRuleListItem) => {
    try {
      await notificationRulesApi.updateRule(rule.id, { is_active: !rule.is_active });
      fetchRules();
    } catch (error) {
      console.error('ルール更新エラー:', error);
    }
  };

  // 重要度に応じたアイコンと色
  const getSeverityStyle = (severity: string) => {
    switch (severity) {
      case 'critical':
        return { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' };
      case 'warning':
        return { icon: AlertTriangle, color: 'text-yellow-600', bg: 'bg-yellow-50' };
      default:
        return { icon: Info, color: 'text-blue-600', bg: 'bg-blue-50' };
    }
  };

  const totalPages = Math.ceil(totalNotifications / 20);

  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold mb-6">通知センター</h1>

      {/* フィルター */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">法人</label>
            <select
              className="w-full border border-gray-300 rounded-md px-3 py-2"
              value={selectedCompanyId || ''}
              onChange={(e) => setSelectedCompanyId(Number(e.target.value))}
            >
              <option value="">選択してください</option>
              {accessibleCompanies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.display_name || company.name}
                </option>
              ))}
            </select>
          </div>
          {viewMode === 'notifications' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">フィルター</label>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value={filterUnread === undefined ? '' : filterUnread ? 'unread' : 'read'}
                onChange={(e) => {
                  if (e.target.value === '') setFilterUnread(undefined);
                  else if (e.target.value === 'unread') setFilterUnread(true);
                  else setFilterUnread(false);
                  setCurrentPage(1);
                }}
              >
                <option value="">すべて</option>
                <option value="unread">未読のみ</option>
                <option value="read">既読のみ</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* タブ切り替え */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          className={`px-4 py-2 font-medium flex items-center gap-2 ${
            viewMode === 'notifications'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setViewMode('notifications')}
        >
          <Bell className="w-4 h-4" />
          通知一覧
          {unreadCount > 0 && (
            <span className="bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
              {unreadCount}
            </span>
          )}
        </button>
        <button
          className={`px-4 py-2 font-medium flex items-center gap-2 ${
            viewMode === 'rules'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setViewMode('rules')}
        >
          <Settings className="w-4 h-4" />
          通知ルール
        </button>
      </div>

      {/* 通知一覧ビュー */}
      {viewMode === 'notifications' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 通知リスト */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold">通知履歴</h2>
              {unreadCount > 0 && (
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={markAllAsRead}
                >
                  すべて既読にする
                </button>
              )}
            </div>
            <div className="divide-y divide-gray-200">
              {isLoadingNotifications ? (
                <div className="p-8 text-center text-gray-500">読み込み中...</div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center text-gray-500">通知はありません</div>
              ) : (
                notifications.map((notification) => {
                  const style = getSeverityStyle(notification.severity);
                  const Icon = style.icon;
                  return (
                    <div
                      key={notification.id}
                      className={`p-4 cursor-pointer hover:bg-gray-50 ${
                        !notification.is_read ? 'bg-blue-50' : ''
                      } ${selectedNotification?.id === notification.id ? 'ring-2 ring-blue-500' : ''}`}
                      onClick={() => viewNotificationDetail(notification)}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-full ${style.bg}`}>
                          <Icon className={`w-4 h-4 ${style.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <h3 className={`font-medium truncate ${!notification.is_read ? 'text-gray-900' : 'text-gray-600'}`}>
                              {notification.title}
                            </h3>
                            {!notification.is_read && (
                              <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mt-1">
                            {new Date(notification.created_at).toLocaleString('ja-JP')}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            {/* ページネーション */}
            {totalPages > 1 && (
              <div className="p-4 border-t border-gray-200 flex justify-center gap-2">
                <button
                  className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  前へ
                </button>
                <span className="px-3 py-1">
                  {currentPage} / {totalPages}
                </span>
                <button
                  className="px-3 py-1 border border-gray-300 rounded disabled:opacity-50"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  次へ
                </button>
              </div>
            )}
          </div>

          {/* 通知詳細 */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">通知詳細</h2>
            </div>
            <div className="p-4">
              {isLoadingDetail ? (
                <div className="text-center text-gray-500">読み込み中...</div>
              ) : selectedNotification ? (
                <div>
                  <div className={`p-3 rounded-lg ${getSeverityStyle(selectedNotification.severity).bg} mb-4`}>
                    <h3 className="font-semibold">{selectedNotification.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {new Date(selectedNotification.created_at).toLocaleString('ja-JP')}
                    </p>
                  </div>
                  <div className="prose prose-sm max-w-none">
                    <p className="whitespace-pre-wrap">{selectedNotification.message}</p>
                  </div>
                  {selectedNotification.extra_data && (
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">詳細情報</h4>
                      <dl className="text-sm space-y-1">
                        {Object.entries(selectedNotification.extra_data).map(([key, value]) => (
                          <div key={key} className="flex">
                            <dt className="text-gray-500 w-24">{key}:</dt>
                            <dd className="text-gray-900">{String(value)}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  )}
                  <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
                    {selectedNotification.is_read ? (
                      <>
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        既読
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4" />
                        未読
                      </>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center text-gray-500">
                  通知を選択してください
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ルール管理ビュー */}
      {viewMode === 'rules' && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold">通知ルール一覧</h2>
            <button
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              onClick={() => openRuleModal()}
              disabled={!selectedCompanyId}
            >
              新規作成
            </button>
          </div>
          <div className="p-4">
            {isLoadingRules ? (
              <div className="text-center py-8">読み込み中...</div>
            ) : rules.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                通知ルールがありません。新規作成してください。
              </div>
            ) : (
              <div className="space-y-4">
                {rules.map((rule) => (
                  <div key={rule.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-lg">{rule.name}</h3>
                          <span
                            className={`px-2 py-0.5 rounded text-xs ${
                              rule.is_active
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {rule.is_active ? '有効' : '無効'}
                          </span>
                        </div>
                        <div className="mt-2 text-sm text-gray-600 space-y-1">
                          <p>
                            <span className="font-medium">タイプ:</span>{' '}
                            {RULE_TYPE_OPTIONS.find((o) => o.value === rule.rule_type)?.label}
                          </p>
                          <p>
                            <span className="font-medium">指標:</span>{' '}
                            {METRIC_OPTIONS.find((o) => o.value === rule.metric)?.label}
                          </p>
                          <p>
                            <span className="font-medium">条件:</span>{' '}
                            {CONDITION_OPTIONS.find((o) => o.value === rule.condition)?.label}{' '}
                            {rule.threshold}
                          </p>
                          {rule.last_triggered_at && (
                            <p className="text-gray-400">
                              最終発火: {new Date(rule.last_triggered_at).toLocaleString('ja-JP')}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          className={`text-sm ${
                            rule.is_active ? 'text-yellow-600 hover:text-yellow-800' : 'text-green-600 hover:text-green-800'
                          }`}
                          onClick={() => toggleRuleActive(rule)}
                        >
                          {rule.is_active ? '無効化' : '有効化'}
                        </button>
                        <button
                          className="text-blue-600 hover:text-blue-800 text-sm"
                          onClick={() => openRuleModal(rule)}
                        >
                          編集
                        </button>
                        <button
                          className="text-red-600 hover:text-red-800 text-sm"
                          onClick={() => deleteRule(rule.id)}
                        >
                          削除
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ルール作成/編集モーダル */}
      {showRuleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">
                {editingRule ? '通知ルール編集' : '通知ルール作成'}
              </h2>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ルール名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={ruleName}
                  onChange={(e) => setRuleName(e.target.value)}
                  placeholder="例: 日次値低下アラート"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">説明</label>
                <textarea
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={ruleDescription}
                  onChange={(e) => setRuleDescription(e.target.value)}
                  rows={2}
                  placeholder="ルールの説明（任意）"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ルールタイプ
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    value={ruleType}
                    onChange={(e) => setRuleType(e.target.value)}
                  >
                    {RULE_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    監視指標
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    value={metric}
                    onChange={(e) => setMetric(e.target.value)}
                  >
                    {METRIC_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    条件
                  </label>
                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    value={condition}
                    onChange={(e) => setCondition(e.target.value)}
                  >
                    {CONDITION_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    閾値
                  </label>
                  <input
                    type="number"
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    value={threshold}
                    onChange={(e) => setThreshold(Number(e.target.value))}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  通知チャンネル
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={notificationChannels.includes('email')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNotificationChannels([...notificationChannels, 'email']);
                        } else {
                          setNotificationChannels(notificationChannels.filter((c) => c !== 'email'));
                        }
                      }}
                    />
                    <span className="text-sm">メール</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={notificationChannels.includes('slack')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNotificationChannels([...notificationChannels, 'slack']);
                        } else {
                          setNotificationChannels(notificationChannels.filter((c) => c !== 'slack'));
                        }
                      }}
                    />
                    <span className="text-sm">Slack</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                onClick={() => setShowRuleModal(false)}
              >
                キャンセル
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                onClick={saveRule}
                disabled={!ruleName}
              >
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
